import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { AppPrimaryButton } from 'shared/components/AppComponents';
import AddIcon from '@mui/icons-material/Add';
import { IRiskRegister } from 'modules/risk-management/risk-register/List';
import { Link } from 'react-router-dom';
import LaunchIcon from '@mui/icons-material/Launch';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import AddRegisterRisk from 'modules/risk-management/risk-register/add-register/Main';
import { RiskRegisterService } from 'services/RiskRegisterService';
import { Utils } from 'shared/helpers';
import { BaseApplicationState } from 'state/baseapplication/Reducer';
import { useSelector } from 'react-redux';

// const dateFns = new DateFnsAdapter();

interface LinkedRiskRegistersProps {
  aiSystemId?: string;
  datasetId?: string;
  type: 'Dataset' | 'AI';
  riskRegisters: IRiskRegister[];
  reloadDetail: () => void;
}

const LinkedRiskRegisters: React.FC<LinkedRiskRegistersProps> = (props) => {
  const { riskRegisters, reloadDetail, aiSystemId } = props;
  const [isLoadingDetail] = useState(false);
  const [showAddRisk, setShowAddRisk] = useState(false);
  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );

  // API Calls
  const linkRiskRegister = (riskId: string): void => {
    RiskRegisterService.linkAISystem(riskId, {
      aisystemId: aiSystemId,
      action: 'link',
    })
      .then(() => {
        reloadDetail();
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      });
  };

  // UI Actions
  // Hooks
  // UI Elements
  return (
    <Box>
      {isLoadingDetail && (
        <Box className="empty-content">
          <CircularProgress />
        </Box>
      )}
      {!isLoadingDetail && (
        <Box className="sub-box">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            className="mb-4 heading"
          >
            <Typography variant="h3">AI Risk Management</Typography>
            {orgACLConfig.aiInventory?.includes(
              'aiInventory.aiSystem.aiInventory.view.riskAssessment.addQuickAIRisk'
            ) && (
              <AppPrimaryButton
                startIcon={<AddIcon />}
                onClick={() => {
                  setShowAddRisk(true);
                }}
                className="btn-small"
                title="Quick Add AI Risk"
              />
            )}
          </Stack>
          <Box className="sub-body">
            {riskRegisters.length > 0 && (
              <Typography className="mb-2" variant="h4">
                Associated Risks ({riskRegisters.length})
              </Typography>
            )}
            <Grid container spacing={5}>
              {riskRegisters.map((risk) => (
                <Grid xs={12} item>
                  <Stack
                    justifyContent="space-between"
                    style={{
                      border: '1px solid lightgray',
                      borderRadius: '10px',
                      padding: '10px 20px',
                      height: '100%',
                    }}
                  >
                    <Box style={{ flex: 1 }}>
                      <Stack spacing={4} direction="row" alignItems="center">
                        <LaunchIcon />
                        <Link to={`/risk-register/detail/${risk._id}`}>
                          <Typography sx={{ color: 'black' }} variant="h3">
                            {risk.riskTitle}
                          </Typography>
                        </Link>
                      </Stack>
                      <Stack
                        spacing={2}
                        direction="row"
                        alignItems="center"
                        className="mt-4"
                      >
                        <Chip label={risk.riskType} size="small" />
                        <Chip label={risk.likelihood} size="small" />
                        <Chip label={risk.impact} size="small" />
                      </Stack>
                      <Typography className="mt-2">
                        {risk.riskDescription}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
              ))}
            </Grid>
            {riskRegisters.length === 0 && (
              <Box className="empty-content">
                <Typography variant="h3">No risks linked</Typography>
                {orgACLConfig.aiInventory?.includes(
                  'aiInventory.aiSystem.aiInventory.view.riskAssessment.addQuickAIRisk'
                ) && (
                  <AppPrimaryButton
                    className="mt-4"
                    title="Quick Add Risk"
                    onClick={() => {
                      setShowAddRisk(true);
                    }}
                  />
                )}
              </Box>
            )}
          </Box>
        </Box>
      )}

      <Dialog maxWidth="md" fullWidth open={showAddRisk}>
        <DialogTitle>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h3">Quick Add AI Risk</Typography>
            </Box>
            <IconButton onClick={() => setShowAddRisk(false)}>
              <HighlightOffIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <AddRegisterRisk
            quickAdd
            onClose={(response) => {
              if (typeof response === 'object') {
                const { data } = response.data;
                linkRiskRegister(data._id);
              }
              setShowAddRisk(false);
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default LinkedRiskRegisters;
